import MuiTooltip, {
  tooltipClasses,
  TooltipProps as MuiTooltipProps,
  TooltipPropsColorOverrides,
} from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import { forwardRef } from 'react';
import { OverridableStringUnion } from '@mui/types';
import colors from '../theme/colors';

declare module '@mui/material/Tooltip' {
  interface TooltipPropsColorOverrides {
    primary: true;
    default: true;
  }
}

interface TooltipOwnerState {
  color?: OverridableStringUnion<'default' | 'primary', TooltipPropsColorOverrides>;
  size?: 'medium' | 'large';
  arrow?: MuiTooltipProps['arrow'];
}

interface TooltipProps extends Omit<MuiTooltipProps, 'arrow' | 'color'>, TooltipOwnerState {
  className?: string;
  'data-elm'?: string;
}

interface TooltipRootProps {
  ownerState: TooltipOwnerState;
}

interface TooltipRootProps extends Omit<TooltipProps, 'data-elm'> {}

const largeArrowSize = {
  w: 30,
  h: 16,
  m: 3,
};

const TooltipRoot = styled(
  ({ className, ...props }: TooltipRootProps) => {
    const { children, ...otherProps } = props;

    return (
      <MuiTooltip enterTouchDelay={100} {...otherProps} classes={{ popper: className }}>
        {children}
      </MuiTooltip>
    );
  },
  {
    shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'ownerState',
  }
)<TooltipRootProps>(({ theme, ownerState }) => ({
  [`.${tooltipClasses.tooltip}`]: {
    padding: '10px',
    textAlign: 'center',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
  },
  ...(ownerState.size === 'large' && {
    [`.${tooltipClasses.tooltip}`]: {
      padding: '20px 16px',
    },
    ...(ownerState.arrow && {
      [`.${tooltipClasses.arrow}`]: {
        width: largeArrowSize.w,
        height: largeArrowSize.h,
      },
      [`&[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]: {
        marginTop: -largeArrowSize.h,
        marginLeft: largeArrowSize.m,
      },
      [`&[data-popper-placement*="top"] .${tooltipClasses.arrow}`]: {
        marginBottom: -largeArrowSize.h,
        marginLeft: -largeArrowSize.m,
      },
      [`&[data-popper-placement*="right"] .${tooltipClasses.arrow}`]: {
        width: largeArrowSize.h,
        height: largeArrowSize.w,
        marginTop: -largeArrowSize.m,
        ...(!(theme.direction === 'rtl')
          ? {
              marginLeft: -largeArrowSize.h,
            }
          : {
              marginRight: -largeArrowSize.h,
            }),
      },
      [`&[data-popper-placement*="left"] .${tooltipClasses.arrow}`]: {
        width: largeArrowSize.h,
        height: largeArrowSize.w,
        marginTop: largeArrowSize.m,
        ...(!(theme.direction === 'rtl')
          ? {
              marginRight: -largeArrowSize.h,
            }
          : {
              marginLeft: -largeArrowSize.h,
            }),
      },
    }),
  }),
  ...(ownerState.color === 'default' && {
    [`& .${tooltipClasses.tooltip}`]: {
      color: colors.basic.white,
      backgroundColor: colors.basic.black,
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: colors.basic.black,
    },
  }),
  ...(ownerState.color === 'primary' && {
    [`& .${tooltipClasses.tooltip}`]: {
      border: '1px solid',
      borderColor: colors.transparentGray['100'],
      backgroundColor: colors.basic['200'],
      color: colors.brandPrimary['500'],
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: colors.basic['200'],

      '&:before': {
        border: '1px solid',
        borderColor: colors.transparentGray['100'],
      },
    },
  }),
}));

const Tooltip = forwardRef<HTMLTitleElement, TooltipProps>((props, ref) => {
  const {
    arrow = true,
    color = 'default',
    size = 'medium',
    'data-elm': dataElm,
    children,
    slotProps,
    ...otherProps
  } = props;
  const ownerState = {
    arrow,
    color,
    size,
    ...otherProps,
  };

  return (
    <TooltipRoot
      ref={ref}
      arrow={arrow}
      slotProps={{
        ...slotProps,
        popper: {
          ...slotProps?.popper,
          ...{ 'data-elm': dataElm },
        },
      }}
      ownerState={ownerState}
      {...otherProps}
    >
      {children}
    </TooltipRoot>
  );
});

Tooltip.displayName = 'Tooltip';

export type { TooltipProps };
export default Tooltip;
